import { buttonVariants } from "@/app/components/ui/button";
import { linkVariants } from "@/app/components/ui/link-anchor";
import { cn } from "@/lib/utils";
import type {
  ButtonLinkSizeT,
  ButtonLinkTargetT,
  ButtonLinkVariantT,
} from "./button-link-node.types";
import { normalizeButtonLinkHref } from "@/app/utils/helpers/backend/tiptap/button-link-url";

export { normalizeButtonLinkHref } from "@/app/utils/helpers/backend/tiptap/button-link-url";

export type ButtonLinkAttributesT = {
  text: string;
  href: string;
  variant: ButtonLinkVariantT;
  size: ButtonLinkSizeT;
  target: ButtonLinkTargetT;
  rel?: string;
};

const DEFAULT_ATTRIBUTES: ButtonLinkAttributesT = {
  text: "Button",
  href: "#",
  variant: "default",
  size: "default",
  target: "_self",
};

const VALID_VARIANTS: readonly ButtonLinkVariantT[] = [
  "default",
  "secondary",
  "outline",
  "ghost",
  "destructive",
  "link",
];

const VALID_SIZES: readonly ButtonLinkSizeT[] = [
  "default",
  "xs",
  "sm",
  "md",
  "lg",
];

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function coerceVariant(value: unknown): ButtonLinkVariantT {
  if (isNonEmptyString(value)) {
    const normalized = value.trim();
    if (VALID_VARIANTS.includes(normalized as ButtonLinkVariantT)) {
      return normalized as ButtonLinkVariantT;
    }
  }
  return DEFAULT_ATTRIBUTES.variant;
}

function coerceSize(value: unknown): ButtonLinkSizeT {
  if (isNonEmptyString(value)) {
    const normalized = value.trim();
    if (VALID_SIZES.includes(normalized as ButtonLinkSizeT)) {
      return normalized as ButtonLinkSizeT;
    }
  }
  return DEFAULT_ATTRIBUTES.size;
}

function coerceTarget(value: unknown): ButtonLinkTargetT {
  if (isNonEmptyString(value) && value.trim() === "_blank") {
    return "_blank";
  }
  return "_self";
}

function pickStringAttribute(
  attributes: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = attributes[key];
    if (isNonEmptyString(value)) {
      return value;
    }
  }
  return undefined;
}

export function mapButtonSizeToLinkSize(
  size: ButtonLinkSizeT
): "sm" | "default" | "md" | "lg" {
  if (size === "xs" || size === "sm") {
    return "sm";
  }
  if (size === "md") {
    return "md";
  }
  if (size === "lg") {
    return "lg";
  }
  return "default";
}

export function extractButtonLinkAttributes(
  rawAttributes: Record<string, unknown>
): ButtonLinkAttributesT {
  const text =
    pickStringAttribute(rawAttributes, ["text", "data-text", "dataText"]) ||
    DEFAULT_ATTRIBUTES.text;

  const href =
    normalizeButtonLinkHref(
      pickStringAttribute(rawAttributes, ["href", "data-href", "dataHref"])
    ) ?? DEFAULT_ATTRIBUTES.href;

  const variant = coerceVariant(
    pickStringAttribute(rawAttributes, [
      "variant",
      "data-variant",
      "dataVariant",
    ])
  );

  const size = coerceSize(
    pickStringAttribute(rawAttributes, ["size", "data-size", "dataSize"])
  );

  const target = coerceTarget(
    pickStringAttribute(rawAttributes, ["target", "data-target", "dataTarget"])
  );

  const rel = pickStringAttribute(rawAttributes, ["rel"]);

  return {
    text,
    href,
    variant,
    size,
    target,
    rel: isNonEmptyString(rel) ? rel : undefined,
  };
}

export function getButtonLinkClassNames(
  attributes: Pick<ButtonLinkAttributesT, "variant" | "size">
): string {
  const baseClass = "tiptap-button-link";

  if (attributes.variant === "link") {
    const linkSize = mapButtonSizeToLinkSize(attributes.size);
    const classes = linkVariants({
      variant: "default",
      underline: "none",
      size: linkSize,
    });

    return cn(baseClass, "tiptap-button-link--link", classes);
  }

  const buttonClasses = buttonVariants({
    variant: attributes.variant,
    size: attributes.size,
  });

  return cn(baseClass, "tiptap-button-link--button", buttonClasses);
}

export function buildButtonLinkDataAttributes(
  attributes: ButtonLinkAttributesT
): Record<string, string> {
  const dataAttributes: Record<string, string> = {
    "data-text": attributes.text,
    "data-variant": attributes.variant,
    "data-size": attributes.size,
  };

  if (attributes.target === "_blank") {
    dataAttributes["data-target"] = "_blank";
  }

  return dataAttributes;
}
